import React from "react";
import AdminNavbar from "../Navbars/AdminNavbar.js";
import PortalSidebar from "./PortalSidebar";
import { PortalApis } from "../../apis/PortalApis";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { FaKey, FaCopy, FaSync, FaEye, FaEyeSlash, FaCheck, FaRedo } from "react-icons/fa";


export default function PortalApiKeys() {

    const [apiKeys, setApiKeys] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [rotateLoader, setRotateLoader] = React.useState<any>(null);
    const [revealedKeys, setRevealedKeys] = React.useState<Record<number, { api_key: string; api_secret: string } | null>>({});
    const [revealLoading, setRevealLoading] = React.useState<Record<number, boolean>>({});
    const [copiedField, setCopiedField] = React.useState<string>('');
    const [showRegenModal, setShowRegenModal] = React.useState<any>(null);
    const [regenResult, setRegenResult] = React.useState<any>(null);

    React.useEffect(() => {
        loadKeys();
    }, []);

    const loadKeys = () => {
        setLoading(true);
        PortalApis.getApiKeys().then(
            (response: any) => {
                setLoading(false);
                if (response?.data?.data) {
                    setApiKeys(response.data.data);
                }
            }
        ).catch(function (error) {
            setLoading(false);
            console.error('Error fetching keys:', error);
        });
    };

    // ========== REVEAL FULL KEY ==========
    const revealKey = (keyId: number) => {
        // If already revealed, toggle hide
        if (revealedKeys[keyId]) {
            setRevealedKeys(prev => ({ ...prev, [keyId]: null }));
            return;
        }

        setRevealLoading(prev => ({ ...prev, [keyId]: true }));
        PortalApis.revealApiKey(keyId).then(
            (response: any) => {
                setRevealLoading(prev => ({ ...prev, [keyId]: false }));
                if (response?.data?.data) {
                    setRevealedKeys(prev => ({
                        ...prev,
                        [keyId]: {
                            api_key: response.data.data.api_key,
                            api_secret: response.data.data.api_secret,
                        }
                    }));
                }
            }
        ).catch(function (error) {
            setRevealLoading(prev => ({ ...prev, [keyId]: false }));
            const msg = error?.response?.data?.message || 'Failed to reveal key';
            if (error?.response?.data?.error_code === 'LEGACY_KEY') {
                toast.warn('This is a legacy key. Please regenerate to get a viewable version.');
            } else {
                toast.error(msg);
            }
        });
    };

    // ========== COPY ==========
    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        toast.info('Copied to clipboard');
        setTimeout(() => setCopiedField(''), 2000);
    };

    // ========== REGENERATE ==========
    const handleRegenerate = () => {
        if (!showRegenModal) return;
        setRotateLoader(showRegenModal.id);

        PortalApis.regenerateApiKey(showRegenModal.id).then(
            (response: any) => {
                setRotateLoader(null);
                if (response?.data?.data) {
                    setRegenResult(response.data.data);
                    // Clear revealed keys since old key is revoked
                    setRevealedKeys({});
                    loadKeys();
                    toast.success('API key regenerated successfully');
                } else {
                    toast.warn(response?.data?.message || 'Failed to regenerate');
                    setShowRegenModal(null);
                }
            }
        ).catch(function (error) {
            setRotateLoader(null);
            toast.error(error?.response?.data?.message || 'Failed to regenerate key');
            setShowRegenModal(null);
        });
    };

    // ========== MASK KEY ==========
    const maskKey = (keyPreview: string) => {
        if (!keyPreview) return '••••••••••••••••';
        return keyPreview;
    };

    return (
        <>
            <PortalSidebar title="API Keys" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="API Keys" />
                <div className='lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen'>

                    <div className="lg:pt-5 pt-3">
                        <h3 className="text-[20px] font-semibold">Your API Credentials</h3>
                        <p className="text-[13px] font-normal text-[#999999]">View and manage your API key and secret. Click reveal to see the full credentials.</p>
                    </div>

                    {/* Security Warning */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 mt-4">
                        <p className="text-[13px] text-yellow-800">
                            <strong>Security:</strong> Your API key and secret grant access to your account. Do not expose them in client-side code, public repositories, or share with unauthorized users.
                        </p>
                    </div>

                    {/* Keys List */}
                    <div className="mt-6 space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-16">
                                <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                            </div>
                        ) : apiKeys.length === 0 ? (
                            <div className="bg-white rounded-[10px] border border-[#E2E8F0] p-10 text-center">
                                <FaKey size={40} className="mx-auto text-gray-300 mb-3" />
                                <p className="text-[14px] text-[#808080]">No API keys found. Your key will appear here once your account is approved.</p>
                            </div>
                        ) : (
                            apiKeys.map((apiKey: any) => (
                                <div key={apiKey.id} className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                                    {/* Header */}
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <FaKey className="text-[#E04343]" size={16} />
                                                <h4 className="text-[16px] font-[600] text-[#263238]">
                                                    {apiKey.name || (apiKey.type === 'partner' ? 'Partner API Key' : 'Customer API Key')}
                                                </h4>
                                                {apiKey.is_active ?
                                                    <b style={{ fontWeight: "500", fontSize: '12px', backgroundColor: '#C9F0D0', color: '#51CF66', borderRadius: '10px', padding: '2px 10px' }}>Active</b> :
                                                    <b style={{ fontWeight: "500", fontSize: '12px', backgroundColor: '#FFDDBD', color: '#FF922B', borderRadius: '10px', padding: '2px 10px' }}>Revoked</b>
                                                }
                                                {apiKey.is_sandbox &&
                                                    <b style={{ fontWeight: "500", fontSize: '12px', backgroundColor: '#E8E3FF', color: '#7C5CFC', borderRadius: '10px', padding: '2px 10px' }}>Sandbox</b>
                                                }
                                            </div>
                                            <p className="text-[12px] text-[#808080] mt-1">Created: {apiKey.created_at ? new Date(apiKey.created_at).toLocaleDateString() : 'N/A'}</p>
                                        </div>

                                        {apiKey.is_active && (
                                            <button onClick={() => setShowRegenModal(apiKey)}
                                                className="text-[#E04343] bg-[#FF3B300D] py-[8px] px-[16px] text-[12px] rounded-[5px] flex items-center gap-1 hover:bg-[#E04343] hover:text-white transition">
                                                <FaRedo size={12} />
                                                Regenerate Key
                                            </button>
                                        )}
                                    </div>

                                    {/* API Key Display */}
                                    <div className="bg-[#F6F9FF] border-[0.5px] border-[#D9D9D9] rounded-[5px] p-4 mb-3">
                                        <div className="flex justify-between items-center">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[11px] text-[#808080] mb-1">API Key</p>
                                                <code className="text-[13px] font-mono text-[#263238] break-all">
                                                    {revealedKeys[apiKey.id]
                                                        ? revealedKeys[apiKey.id]!.api_key
                                                        : maskKey(apiKey.key_preview)
                                                    }
                                                </code>
                                            </div>
                                            <div className="flex gap-2 ml-4 shrink-0">
                                                <button onClick={() => revealKey(apiKey.id)}
                                                    disabled={revealLoading[apiKey.id]}
                                                    className="bg-white border border-[#D1D1D1] p-2 rounded-[5px] hover:bg-gray-50"
                                                    title={revealedKeys[apiKey.id] ? 'Hide' : 'Reveal full key'}>
                                                    {revealLoading[apiKey.id]
                                                        ? <Oval visible={true} height="14" width="14" color="#E04343" secondaryColor="#FCBF94" />
                                                        : revealedKeys[apiKey.id]
                                                            ? <FaEyeSlash size={14} />
                                                            : <FaEye size={14} />
                                                    }
                                                </button>
                                                {revealedKeys[apiKey.id] && (
                                                    <button
                                                        onClick={() => copyToClipboard(revealedKeys[apiKey.id]!.api_key, `key-${apiKey.id}`)}
                                                        className="bg-[#E04343] text-white p-2 rounded-[5px] hover:bg-[#c73838]"
                                                        title="Copy API Key">
                                                        {copiedField === `key-${apiKey.id}` ? <FaCheck size={14} /> : <FaCopy size={14} />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* API Secret Display */}
                                    <div className="bg-[#FFF9F6] border-[0.5px] border-[#D9D9D9] rounded-[5px] p-4">
                                        <div className="flex justify-between items-center">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[11px] text-[#808080] mb-1">API Secret</p>
                                                <code className="text-[13px] font-mono text-[#263238] break-all">
                                                    {revealedKeys[apiKey.id]
                                                        ? revealedKeys[apiKey.id]!.api_secret
                                                        : '••••••••••••••••••••••••••••••••'
                                                    }
                                                </code>
                                            </div>
                                            <div className="flex gap-2 ml-4 shrink-0">
                                                {revealedKeys[apiKey.id] && (
                                                    <button
                                                        onClick={() => copyToClipboard(revealedKeys[apiKey.id]!.api_secret, `secret-${apiKey.id}`)}
                                                        className="bg-[#E04343] text-white p-2 rounded-[5px] hover:bg-[#c73838]"
                                                        title="Copy API Secret">
                                                        {copiedField === `secret-${apiKey.id}` ? <FaCheck size={14} /> : <FaCopy size={14} />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Legacy key notice */}
                                    {!apiKey.has_full_key && (
                                        <div className="bg-orange-50 border-l-4 border-orange-400 rounded p-3 mt-3">
                                            <p className="text-[12px] text-orange-700">
                                                This key was created before encrypted storage was enabled. Click <strong>Regenerate Key</strong> above to get a viewable version.
                                            </p>
                                        </div>
                                    )}

                                    {/* Key Metadata */}
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                                        <div>
                                            <p className="text-[11px] text-[#808080]">Rate Limit</p>
                                            <p className="text-[13px] font-[500]">{apiKey.rate_limit || '120 req/min'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] text-[#808080]">Total Requests</p>
                                            <p className="text-[13px] font-[500]">{(apiKey.total_requests || 0).toLocaleString()}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] text-[#808080]">Last Used</p>
                                            <p className="text-[13px] font-[500]">{apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString() : 'Never'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] text-[#808080]">Expires</p>
                                            <p className="text-[13px] font-[500]">{apiKey.expires_at ? new Date(apiKey.expires_at).toLocaleDateString() : 'No expiry'}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Usage Example */}
                    <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px] mt-6">
                        <h3 className="text-[16px] font-semibold mb-3">How to use your API Key</h3>
                        <div className="bg-[#1a1a2e] rounded-[5px] p-4 overflow-x-auto">
                            <pre className="text-[12px] text-green-400 font-mono whitespace-pre">
                                {`curl -X POST ${'{BASE_URL}'}/api/v1/partner/shipments \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "X-API-Secret: YOUR_API_SECRET" \\
  -d '{
    "origin": { "name": "John", "city": "Lagos", "state": "Lagos" },
    "destination": { "name": "Jane", "city": "Abuja", "state": "FCT" },
    "weight": 2.5
  }'`}
                            </pre>
                        </div>
                    </div>

                </div>
            </div>

            {/* ========== REGENERATE CONFIRMATION MODAL ========== */}
            {showRegenModal && !regenResult && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[420px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold'>Regenerate API Key</h3>
                            <button onClick={() => setShowRegenModal(null)} className="text-gray-400 hover:text-gray-600">✕</button>
                        </div>
                        <div className='p-5'>
                            <div className="bg-red-50 border-l-4 border-red-500 rounded p-3 mb-4">
                                <p className="text-[13px] text-red-800">
                                    <strong>Warning:</strong> This will revoke your current API key immediately. Any integrations using the old key will stop working. This cannot be undone.
                                </p>
                            </div>
                            <p className="text-[13px] text-[#666] mb-4">
                                A new API key and secret will be generated. Make sure to update your integrations with the new credentials.
                            </p>
                            <div className="flex gap-3">
                                <button onClick={() => setShowRegenModal(null)}
                                    className="flex-1 border border-[#D1D1D1] text-[#666] py-2.5 rounded-[5px] text-[13px] hover:bg-gray-50">
                                    Cancel
                                </button>
                                <button onClick={handleRegenerate} disabled={!!rotateLoader}
                                    className="flex-1 bg-[#E04343] text-white py-2.5 rounded-[5px] text-[13px] hover:bg-[#c73838] disabled:opacity-50 flex justify-center gap-2">
                                    Regenerate
                                    <Oval visible={!!rotateLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== NEW KEY RESULT MODAL ========== */}
            {regenResult && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[520px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold text-green-700'>New API Key Generated</h3>
                        </div>
                        <div className='p-5'>
                            <div className="bg-green-50 border-l-4 border-green-500 rounded p-3 mb-4">
                                <p className="text-[13px] text-green-800">
                                    Your new API credentials are ready. You can always view them from this dashboard.
                                </p>
                            </div>

                            {/* New Key */}
                            <div className="mb-3">
                                <p className="text-[11px] text-[#808080] mb-1">New API Key</p>
                                <div className="bg-[#F6F9FF] border border-[#D9D9D9] rounded-[5px] p-3 flex justify-between items-center">
                                    <code className="text-[12px] font-mono text-[#263238] break-all flex-1">{regenResult.new_key?.api_key}</code>
                                    <button onClick={() => copyToClipboard(regenResult.new_key?.api_key, 'new-key')}
                                        className="ml-2 bg-[#E04343] text-white p-1.5 rounded-[5px] hover:bg-[#c73838] shrink-0">
                                        {copiedField === 'new-key' ? <FaCheck size={12} /> : <FaCopy size={12} />}
                                    </button>
                                </div>
                            </div>

                            {/* New Secret */}
                            <div className="mb-4">
                                <p className="text-[11px] text-[#808080] mb-1">New API Secret</p>
                                <div className="bg-[#FFF9F6] border border-[#D9D9D9] rounded-[5px] p-3 flex justify-between items-center">
                                    <code className="text-[12px] font-mono text-[#263238] break-all flex-1">{regenResult.new_key?.api_secret}</code>
                                    <button onClick={() => copyToClipboard(regenResult.new_key?.api_secret, 'new-secret')}
                                        className="ml-2 bg-[#E04343] text-white p-1.5 rounded-[5px] hover:bg-[#c73838] shrink-0">
                                        {copiedField === 'new-secret' ? <FaCheck size={12} /> : <FaCopy size={12} />}
                                    </button>
                                </div>
                            </div>

                            <button onClick={() => { setRegenResult(null); setShowRegenModal(null); }}
                                className="w-full bg-[#263238] text-white py-2.5 rounded-[5px] text-[13px] hover:bg-[#1a2228]">
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={true} />
        </>
    );
}